/**
 * AttemptsCell - how many tries have been made ("2 of 6", contacts.attempt_count over the study's
 * cadence ceiling) and the attempt menu for this lead (docs/plans/crm-recruitment-list-plan.md
 * Part A sections 3 step 5, 4 and 6, B4). A logged attempt counts exactly as a softphone call does
 * (the server writes a real call row), so the next refresh brings the cadence's next try; until
 * then the row takes what the server's `effect` says changed, and nothing more.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { contactName } from '../../../types';
import AttemptMenu from '../../AttemptMenu';
import { triesOffReason } from '../../recruitLogic';
import type {
  RecruitAttemptOutcome,
  RecruitAttemptRequest,
  RecruitAttemptResponse,
  RecruitRow,
  RecruitRowChangeHandler,
  RecruitStudy,
} from '../../types';

export interface AttemptsCellProps {
  row: RecruitRow;
  /** The study the list is for: the attempt route's :studyId and its cadence ceiling. */
  study: Pick<RecruitStudy, 'id' | 'max_attempts'>;
  onRowChange: RecruitRowChangeHandler;
  /** Sheet keyboard: Enter opened this cell (open the attempt menu). */
  editing?: boolean;
  /** The menu closed. */
  onEditEnd?: () => void;
  className?: string;
}

/** Mirrors backend crmOutreachBuckets DO_NOT_CONTACT_TAG (the Flags column's "Do not contact"). */
export const DO_NOT_CONTACT_TAG = 'do_not_contact';

/**
 * Why the tries (No answer ... Try later, and booking a call back) are unavailable on this row, or
 * null when they are available: recruitLogic triesOffReason over the row's contact and membership
 * (fails closed; Wrong number and Asked us to stop stay available). The Next-up bar asks the same
 * rule of a Work-through item (recruitLogic studyItemTriesReason).
 */
export function triesUnavailableReason(row: RecruitRow): string | null {
  return triesOffReason({
    merged: !!row.contact.merged_into_id,
    archived: !!row.contact.archived_at,
    status: row.membership.status,
  });
}

/**
 * The row after a logged attempt, from the server's `effect` alone (the confirmation line's rule:
 * never more than happened). A new callback takes the time the recruiter picked; it replaces the
 * row's callback only when it is sooner. `meId` is the default assignee (the caller).
 */
export function applyAttemptEffect(
  row: RecruitRow,
  outcome: RecruitAttemptOutcome,
  res: RecruitAttemptResponse,
  request: RecruitAttemptRequest,
  meId: string | null,
): RecruitRow {
  const effect = res.effect || {};
  let contact = row.contact;
  let flags = row.flags;
  let callback = row.callback;
  if (typeof effect.attempt_count === 'number') contact = { ...contact, attempt_count: effect.attempt_count };
  if (effect.next_action_at !== undefined) contact = { ...contact, next_action_at: effect.next_action_at };
  if (effect.snoozed_until !== undefined) {
    contact = { ...contact, snoozed_until: effect.snoozed_until };
    flags = { ...flags, snoozed_until: effect.snoozed_until };
  }
  if (Array.isArray(effect.tags) && effect.tags.length > 0) {
    const tags = Array.from(new Set([...(contact.tags || []), ...effect.tags]));
    contact = { ...contact, tags };
    if (tags.includes(DO_NOT_CONTACT_TAG)) flags = { ...flags, do_not_contact: true };
  }
  if (effect.opted_out === true) {
    contact = { ...contact, sms_opt_out: true };
    flags = { ...flags, opted_out: true };
  }
  const slot = request.callback?.slot_start;
  if (outcome === 'call_back' && effect.callback_id && slot) {
    const sooner = !callback || Date.parse(slot) < Date.parse(callback.scheduled_at);
    if (sooner) {
      callback = {
        id: effect.callback_id,
        scheduled_at: slot,
        assignee_user_id: request.callback?.assignee_user_id ?? meId,
        assignee_name: null,
      };
    }
  }
  if (contact === row.contact && flags === row.flags && callback === row.callback) return row;
  return { ...row, contact, flags, callback };
}

export function AttemptsCell({ row, study, onRowChange, editing = false, onEditEnd, className = '' }: AttemptsCellProps) {
  const { user } = useAuth();
  const meId = user?.id ?? null;
  const contactId = row.contact.id;
  const made = typeof row.contact.attempt_count === 'number' ? row.contact.attempt_count : 0;
  const max = study.max_attempts;

  // Enter from the sheet: each new editing turn opens the menu once.
  const [signal, setSignal] = useState(0);
  useEffect(() => {
    if (editing) setSignal((n) => n + 1);
  }, [editing]);

  const onEditEndRef = useRef(onEditEnd);
  onEditEndRef.current = onEditEnd;
  const onClose = useCallback(() => onEditEndRef.current?.(), []);

  const onLogged = useCallback((outcome: RecruitAttemptOutcome, res: RecruitAttemptResponse, request: RecruitAttemptRequest) => {
    onRowChange(contactId, (r) => applyAttemptEffect(r, outcome, res, request, meId));
  }, [contactId, meId, onRowChange]);

  return (
    <div className={`flex min-w-0 items-center gap-2 ${className}`}>
      <span
        title={`${made} of ${max} tries made`}
        className="whitespace-nowrap text-xs tabular-nums text-gray-600 dark:text-gray-300"
      >
        {made} of {max}
      </span>
      <AttemptMenu
        studyId={study.id}
        contactId={contactId}
        surface="sheet"
        maxAttempts={max}
        triesDisabledReason={triesUnavailableReason(row)}
        openSignal={signal}
        onClose={onClose}
        onLogged={onLogged}
        name={contactName(row.contact)}
      />
    </div>
  );
}

export default AttemptsCell;
